import { Row, Col, Dropdown } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faChevronDown } from '@fortawesome/free-solid-svg-icons';
import { Extension, useEditor, useEditorState, EditorContent, type Editor } from '@tiptap/react';
import StarterKit from '@tiptap/starter-kit';
import Link from '@tiptap/extension-link';
import { Table } from '@tiptap/extension-table';
import { TableRow } from '@tiptap/extension-table-row';
import { TableCell } from '@tiptap/extension-table-cell';
import { TableHeader } from '@tiptap/extension-table-header';
import { Underline } from '@tiptap/extension-underline';
import { Markdown } from 'tiptap-markdown';
import { useEffect, useCallback } from 'react';
import { observer } from 'mobx-react-lite';

import type { EditableNodeDefinition } from '../../edit/EditableNodeDefinition';
import './Documentation.scss';

/**
 * The toolbar's active and disabled states are all derived from where the caret
 * is, so they have to survive a selection change — a transaction that leaves the
 * document alone.
 *
 * NOTE: neither of the two things that re-render this tab fires on such a
 * transaction. `useEditor` does not re-render on transactions (TipTap 3 leaves
 * `shouldRerenderOnTransaction` off), and the `observer` on the tab only reacts
 * when `onUpdate` writes markdown into the model, i.e. when the *content*
 * changes. Read the flags through `useEditorState` instead, which subscribes the
 * toolbar to the editor directly; its default deep-equality check keeps that to
 * one render per flag that actually changed.
 */
export const useToolbarState = (editor: Editor | null) =>
  useEditorState({
    editor,
    selector: ({ editor: current }) =>
      current === null
        ? null
        : {
            canUndo: current.can().undo(),
            canRedo: current.can().redo(),
            block: current.isActive('heading', { level: 1 })
              ? '1'
              : current.isActive('heading', { level: 2 })
                ? '2'
                : current.isActive('heading', { level: 3 })
                  ? '3'
                  : 'p',
            isBold: current.isActive('bold'),
            isItalic: current.isActive('italic'),
            isUnderline: current.isActive('underline'),
            isLink: current.isActive('link'),
            isBulletList: current.isActive('bulletList'),
            isOrderedList: current.isActive('orderedList'),
            isCodeBlock: current.isActive('codeBlock'),
            inTable: current.isActive('table'),
            inHeaderCell: current.isActive('tableHeader'),
          },
  });

/**
 * Which table operations apply where the caret is.
 *
 * NOTE: kept as data rather than spread through the JSX because three of these
 * are load-bearing, not cosmetic — see `TableMenu`. A guard written inline is a
 * guard the next reader can delete without noticing what it was for, and the
 * only way to check one is to render the menu.
 */
export const tableOpsFor = ({ inTable, inHeaderCell }: { readonly inTable: boolean; readonly inHeaderCell: boolean }) => ({
  insertTable: !inTable,
  addRowBefore: inTable && !inHeaderCell,
  addRowAfter: inTable,
  deleteRow: inTable && !inHeaderCell,
  addColumnBefore: inTable,
  addColumnAfter: inTable,
  deleteColumn: inTable,
  deleteTable: inTable,
});

/**
 * Every table operation, as one menu.
 *
 * NOTE: the set is limited to operations whose result still serialises as a GFM
 * pipe table, and the reason is data loss rather than formatting.
 * `tiptap-markdown` emits a pipe table only while every cell of the first row is
 * a `tableHeader`, no body row holds one, and no cell spans or carries more than
 * one block. Anything else goes to its HTML fallback — and under `html: false`,
 * which is how this editor is configured, that fallback writes the literal
 * placeholder `[table]` rather than any markup. So the table is not reformatted
 * on save, it is replaced and every cell is gone. Three operations reach that
 * state and are gated accordingly:
 *
 *   - `addRowBefore` in the header row inserts *plain* cells above it
 *     (prosemirror-tables falls back to `cell` when its reference row is a
 *     header and the insert sits at the table edge), leaving a body row first.
 *   - `deleteRow` on the header row promotes a body row to first.
 *   - `insertTable` inside a cell nests a table, which markdown cannot express
 *     at all.
 *
 * Merge and split are absent for the same reason: they introduce colspan and
 * rowspan. Documentation.test.tsx pins that fallback, since it is third-party
 * behaviour these guards depend on rather than behaviour of ours.
 */
const TableMenu = ({ editor, inTable, inHeaderCell }: { readonly editor: Editor; readonly inTable: boolean; readonly inHeaderCell: boolean }) => {
  const enabled = tableOpsFor({ inTable, inHeaderCell });
  return (
    <Dropdown>
      <Dropdown.Toggle variant="phoenix-secondary" size="sm" title="Table">
        Table
        <FontAwesomeIcon icon={faChevronDown} className="ms-1 fs-10" />
      </Dropdown.Toggle>
      <Dropdown.Menu>
        <Dropdown.Item disabled={!enabled.insertTable} onClick={() => editor.chain().focus().insertTable({ rows: 3, cols: 3, withHeaderRow: true }).run()}>
          Insert table
        </Dropdown.Item>
        <Dropdown.Divider />
        <Dropdown.Item disabled={!enabled.addRowBefore} onClick={() => editor.chain().focus().addRowBefore().run()}>
          Insert row above
        </Dropdown.Item>
        <Dropdown.Item disabled={!enabled.addRowAfter} onClick={() => editor.chain().focus().addRowAfter().run()}>
          Insert row below
        </Dropdown.Item>
        <Dropdown.Item disabled={!enabled.deleteRow} onClick={() => editor.chain().focus().deleteRow().run()}>
          Delete row
        </Dropdown.Item>
        <Dropdown.Divider />
        <Dropdown.Item disabled={!enabled.addColumnBefore} onClick={() => editor.chain().focus().addColumnBefore().run()}>
          Insert column left
        </Dropdown.Item>
        <Dropdown.Item disabled={!enabled.addColumnAfter} onClick={() => editor.chain().focus().addColumnAfter().run()}>
          Insert column right
        </Dropdown.Item>
        <Dropdown.Item disabled={!enabled.deleteColumn} onClick={() => editor.chain().focus().deleteColumn().run()}>
          Delete column
        </Dropdown.Item>
        <Dropdown.Divider />
        <Dropdown.Item disabled={!enabled.deleteTable} onClick={() => editor.chain().focus().deleteTable().run()}>
          Delete table
        </Dropdown.Item>
      </Dropdown.Menu>
    </Dropdown>
  );
};

/**
 * Exported so a test can assert the wiring itself. Every `disabled` here is a
 * same-typed boolean, so a flag landing on the wrong control type-checks
 * cleanly — and on the Table menu it would re-enable exactly the operations
 * that destroy the table.
 */
export const MenuBar = ({ editor }: { readonly editor: Editor | null }) => {
  const state = useToolbarState(editor);

  if (!editor || !state) return null;

  return (
    <div className="tiptap-toolbar">
      <button type="button" onClick={() => editor.chain().focus().undo().run()} disabled={!state.canUndo} title="Undo">
        ↩
      </button>
      <button type="button" onClick={() => editor.chain().focus().redo().run()} disabled={!state.canRedo} title="Redo">
        ↪
      </button>
      <span className="tiptap-toolbar-divider" />
      {/*
        NOTE: the four block controls below are disabled inside a table because
        a cell holds exactly one paragraph — see `MarkdownTableCell`. Left live
        they would not error, they would silently do nothing, which is the
        discoverability failure this toolbar exists to fix, inverted.
      */}
      <select
        value={state.block}
        disabled={state.inTable}
        onChange={(e) => {
          // Narrowed rather than cast: the option values are the levels the
          // schema allows, and anything else is the paragraph option.
          const level = Number(e.target.value);
          if (level === 1 || level === 2 || level === 3) {
            editor.chain().focus().toggleHeading({ level }).run();
          } else {
            editor.chain().focus().setParagraph().run();
          }
        }}
      >
        <option value="p">Paragraph</option>
        <option value="1">Heading 1</option>
        <option value="2">Heading 2</option>
        <option value="3">Heading 3</option>
      </select>
      <span className="tiptap-toolbar-divider" />
      <button type="button" onClick={() => editor.chain().focus().toggleBold().run()} className={state.isBold ? 'is-active' : ''} title="Bold">
        <strong>B</strong>
      </button>
      <button type="button" onClick={() => editor.chain().focus().toggleItalic().run()} className={state.isItalic ? 'is-active' : ''} title="Italic">
        <em>I</em>
      </button>
      <button type="button" onClick={() => editor.chain().focus().toggleUnderline().run()} className={state.isUnderline ? 'is-active' : ''} title="Underline">
        <u>U</u>
      </button>
      <span className="tiptap-toolbar-divider" />
      <button
        type="button"
        onClick={() => {
          const url = window.prompt('URL');
          if (url) editor.chain().focus().setLink({ href: url }).run();
        }}
        className={state.isLink ? 'is-active' : ''}
        title="Link"
      >
        🔗
      </button>
      <span className="tiptap-toolbar-divider" />
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleBulletList().run()}
        className={state.isBulletList ? 'is-active' : ''}
        disabled={state.inTable}
        title="Bullet list"
      >
        •≡
      </button>
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleOrderedList().run()}
        className={state.isOrderedList ? 'is-active' : ''}
        disabled={state.inTable}
        title="Ordered list"
      >
        1≡
      </button>
      <span className="tiptap-toolbar-divider" />
      <TableMenu editor={editor} inTable={state.inTable} inHeaderCell={state.inHeaderCell} />
      <span className="tiptap-toolbar-divider" />
      <button
        type="button"
        onClick={() => editor.chain().focus().toggleCodeBlock().run()}
        className={state.isCodeBlock ? 'is-active' : ''}
        disabled={state.inTable}
        title="Code block"
      >
        {'<>'}
      </button>
    </div>
  );
};

/**
 * A cell markdown can express holds exactly one paragraph. `tiptap-markdown`
 * hands the whole table to its HTML fallback as soon as any cell has more than
 * one child, and under `html: false` that fallback writes the literal `[table]`
 * — so the table and every cell in it are gone on save.
 *
 * NOTE: this is a schema restriction rather than an Enter keybinding because
 * Enter is not the only way in. TipTap's stock `block+` cell content also
 * admits a list, a code block or a heading, and a list is the worse case: it
 * *replaces* the cell's paragraph, so the cell still has one child and the
 * table still serialises — as a pipe row with a newline inside it, which
 * reparses as a differently shaped table with the neighbouring cells' text run
 * together. Restricting the content makes every one of those commands simply
 * not apply.
 */
const MarkdownTableCell = TableCell.extend({ content: 'paragraph' });
const MarkdownTableHeader = TableHeader.extend({ content: 'paragraph' });

/**
 * Shift+Enter (and Mod+Enter) insert a `hardBreak`, and inside a table cell
 * `tiptap-markdown` routes that through the same HTML fallback — so under
 * `html: false` the literal text `[hardBreak]` is written into the cell. The
 * table itself still serialises, which makes this the quieter failure: the cell
 * keeps eleven characters of noise that reopening cannot undo, because they come
 * back as text. Outside a table a hard break is fine — it serialises as a real
 * markdown line break and round-trips.
 *
 * NOTE: a hard break is inline, so the cell-content restriction above cannot
 * reach it and it has to be refused at the keymap. Returning `true` marks the
 * chord handled, which swallows it; returning `false` inside a table would fall
 * through to `HardBreak`'s own binding and insert one anyway.
 */
const NoHardBreakInTableCells = Extension.create({
  name: 'noHardBreakInTableCells',
  // Ahead of StarterKit's HardBreak, whose bindings these are; the first
  // handler to claim a chord wins.
  priority: 1000,
  addKeyboardShortcuts() {
    const refuseInsideATable = () => this.editor.isActive('table');
    return { 'Shift-Enter': refuseInsideATable, 'Mod-Enter': refuseInsideATable };
  },
});

/**
 * The editor's configuration, exported so a test can serialise through exactly
 * the pipeline the tab uses. `html: false` is what makes the table constraints
 * here and in `TableMenu` matter: a table that falls back to HTML does not come
 * back.
 */
export const DOCUMENTATION_EXTENSIONS = [
  // NOTE: TipTap 3's StarterKit already bundles link and underline, so listing
  // them again registers each twice — which TipTap warns about and leaves the
  // winner undefined. Turn StarterKit's off so the configured Link here is the
  // one that applies.
  StarterKit.configure({ link: false, underline: false }),
  Underline,
  Link.configure({ openOnClick: false }),
  Table.configure({ resizable: true }),
  TableRow,
  MarkdownTableCell,
  MarkdownTableHeader,
  NoHardBreakInTableCells,
  Markdown.configure({
    html: false, // Only allow markdown, no HTML
    transformPastedText: true, // Convert pasted markdown to rich text
  }),
];

/**
 * The Documentation tab: a TipTap rich-text editor bound to the model's `documentation` (markdown). TipTap is uncontrolled —
 * it owns its own document — so this can't be a pure observer view: `onUpdate` pushes the editor's markdown into the model,
 * and the effect pushes external changes (load / reinitialize / duplicate) back into the editor, guarded against echoing.
 */
const Documentation = observer(({ entity }: { readonly entity: EditableNodeDefinition }) => {
  const documentation = entity.documentation ?? '';

  const handleUpdate = useCallback(
    ({ editor }: { editor: NonNullable<ReturnType<typeof useEditor>> }) => {
      entity.documentation = (editor.storage as any).markdown.getMarkdown();
    },
    [entity]
  );

  const editor = useEditor({
    extensions: DOCUMENTATION_EXTENSIONS,
    content: documentation || '',
    onUpdate: handleUpdate,
  });

  useEffect(() => {
    if (!editor) return;
    // Only push in when the external value genuinely differs, so this doesn't echo against onUpdate.
    // emitUpdate:false is load-bearing: TipTap v3 defaults setContent to emitUpdate:true, which would fire onUpdate and write
    // the serializer-canonical markdown back into the model — dirtying (and on save, reformatting) any node whose stored
    // markdown isn't already canonical, just by viewing it. This programmatic sync is not a user edit.
    if ((editor.storage as any).markdown.getMarkdown() !== documentation) {
      editor.commands.setContent(documentation, { emitUpdate: false });
    }
  }, [documentation, editor]);

  return (
    <Row>
      <Col lg="12">
        <div className="tiptap-editor-wrapper">
          <MenuBar editor={editor} />
          <EditorContent editor={editor} className="tiptap-content" />
        </div>
      </Col>
    </Row>
  );
});

export default Documentation;
